import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, IconButton, Modal, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  APIProvider,
  Map,
  Marker,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { boxModalStyle } from "../../BookingStyle";
import { GOOGLE_MAPS_API_KEY } from "../../../../../helpers/googleMaps/GoogleMapsLoader";

const DEFAULT_CENTER = { lat: -12.046374, lng: -77.042793 };

const hasValidPoint = (point) =>
  Number.isFinite(Number(point?.lat)) && Number.isFinite(Number(point?.lng));

const normalizeCoordinate = (coordinate) => {
  if (hasValidPoint(coordinate)) {
    return { lat: Number(coordinate.lat), lng: Number(coordinate.lng) };
  }
  if (
    Number.isFinite(Number(coordinate?.latitude)) &&
    Number.isFinite(Number(coordinate?.longitude))
  ) {
    return {
      lat: Number(coordinate.latitude),
      lng: Number(coordinate.longitude),
    };
  }
  return null;
};

const getLocationAddress = (location) => {
  if (typeof location === "string") return location;
  if (!location) return "";
  return `${location.name ?? ""}${location.url_address ? ` ${location.url_address}` : ""}`;
};

const formatCoordinateAddress = (point) =>
  `${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}`;

const isCoordinateAddress = (value) =>
  /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/.test(String(value || "").trim());

const isMacDevice = () => {
  const platform = navigator.userAgentData?.platform || navigator.platform || "";
  return /mac/i.test(platform);
};

const OriginMap = ({ open, address, coordinate, onAccept }) => {
  const geocodingLibrary = useMapsLibrary("geocoding");
  const geocoder = useMemo(
    () => geocodingLibrary && new geocodingLibrary.Geocoder(),
    [geocodingLibrary],
  );
  const [mapAddress, setMapAddress] = useState(() =>
    getLocationAddress(address),
  );
  const [markerPosition, setMarkerPosition] = useState(() =>
    normalizeCoordinate(coordinate),
  );
  const [hasRequestedGeolocation, setHasRequestedGeolocation] =
    useState(false);

  useEffect(() => {
    setMapAddress(getLocationAddress(address));
    setMarkerPosition(normalizeCoordinate(coordinate));
  }, [address, coordinate]);

  useEffect(() => {
    if (open) return;
    setHasRequestedGeolocation(false);
  }, [open]);

  const resolveAddress = useCallback(async (point) => {
    if (!geocoder) return;

    try {
      const result = await geocoder.geocode({ location: point });
      const address = result.results?.[0]?.formatted_address;
      if (address) {
        setMapAddress(address);
      }
    } catch (error) {
      console.log(error);
    }
  }, [geocoder]);

  useEffect(() => {
    const marker = normalizeCoordinate(coordinate);
    if (!marker || !geocoder) return;
    if (
      String(getLocationAddress(address) || "").trim() &&
      !isCoordinateAddress(getLocationAddress(address))
    ) {
      return;
    }

    const resolveInitialAddress = async () => {
      await resolveAddress(marker);
    };

    resolveInitialAddress();
  }, [address, coordinate, geocoder, resolveAddress]);

  useEffect(() => {
    if (!open || hasRequestedGeolocation) return;
    if (normalizeCoordinate(coordinate)) return;
    if (isMacDevice()) return;
    if (!navigator.geolocation) return;

    setHasRequestedGeolocation(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const nextPoint = {
          lat: coords.latitude,
          lng: coords.longitude,
        };

        setMarkerPosition(nextPoint);
        setMapAddress(formatCoordinateAddress(nextPoint));
        resolveAddress(nextPoint);
      },
      (error) => {
        console.log(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  }, [
    coordinate,
    hasRequestedGeolocation,
    open,
    geocoder,
    resolveAddress,
  ]);

  const handleMapClick = async (event) => {
    const latLng = event.detail.latLng;
    if (!latLng) return;

    const nextPoint = {
      lat: latLng.lat,
      lng: latLng.lng,
    };

    setMarkerPosition(nextPoint);
    setMapAddress(formatCoordinateAddress(nextPoint));
    await resolveAddress(nextPoint);
  };

  const handleAccept = () => {
    const acceptedAddress = String(mapAddress || "").trim();
    if (!markerPosition || !acceptedAddress || isCoordinateAddress(mapAddress)) {
      return;
    }
    onAccept({
      address: acceptedAddress,
      point: markerPosition,
    });
  };

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      <Box sx={{ width: "100%", height: "100%", overflow: "hidden" }}>
        <Map
          center={markerPosition || DEFAULT_CENTER}
          defaultZoom={15}
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={false}
          onClick={handleMapClick}
          style={{ width: "100%", height: "100%" }}
        >
          {markerPosition && <Marker position={markerPosition} />}
        </Map>
      </Box>
      <TextField
        size="small"
        value={mapAddress}
        placeholder="Seleccione una ubicacion en el mapa"
        InputProps={{ readOnly: true }}
        sx={{
          position: "absolute",
          top: 14,
          left: 14,
          width: {
            xs: "calc(100% - 72px)",
            sm: "70%",
          },
          maxWidth: {
            xs: "calc(100% - 72px)",
            sm: "70%",
          },
          bgcolor: "background.paper",
          borderRadius: "4px",
          boxShadow: 3,
        }}
      />
      <Button
        variant="contained"
        color="primary"
        disabled={
          !markerPosition ||
          !String(mapAddress || "").trim() ||
          isCoordinateAddress(mapAddress)
        }
        onClick={handleAccept}
        sx={{
          position: "absolute",
          right: 14,
          bottom: 14,
          minWidth: 140,
          height: 44,
          fontWeight: "bold",
          boxShadow: 3,
        }}
      >
        Aceptar
      </Button>
    </Box>
  );
};

const OriginMapModal = ({
  open,
  onClose,
  address,
  coordinate,
  onLocationAccept,
}) => {
  const handleAcceptLocation = ({ address, point }) => {
    if (onLocationAccept) {
      onLocationAccept({ address, point });
      return;
    }
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          ...boxModalStyle,
          width: {
            xs: "95%",
            sm: "95%",
          },
          height: "95vh",
          p: 0,
          overflow: "hidden",
        }}
      >
        <IconButton
          aria-label="Cerrar"
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 14,
            right: 14,
            width: 34,
            height: 34,
            bgcolor: "error.main",
            color: "common.white",
            zIndex: 1,
            boxShadow: 3,
            "&:hover": {
              bgcolor: "error.dark",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <APIProvider
          apiKey={GOOGLE_MAPS_API_KEY}
          libraries={["geocoding"]}
          onError={(error) => {
            console.log(error);
          }}
        >
          <OriginMap
            open={open}
            address={address}
            coordinate={coordinate}
            onAccept={handleAcceptLocation}
          />
        </APIProvider>
      </Box>
    </Modal>
  );
};

OriginMap.propTypes = {
  open: PropTypes.bool.isRequired,
  address: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  coordinate: PropTypes.object,
  onAccept: PropTypes.func.isRequired,
};

OriginMapModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  address: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  coordinate: PropTypes.object,
  onLocationAccept: PropTypes.func,
};

export default OriginMapModal;
